import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { kr, dato, idag } from '../lib/format';
import { useSort } from '../lib/sort';
import { PageHeader, Card, StatCard, Modal, Tabs } from '../components/ui';
import Kommentarer from '../components/Kommentarer';
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown, Wallet, Paperclip, Lock, RotateCcw, Sparkles, X, Eye, Search, Inbox, UploadCloud, Mail, Copy } from 'lucide-react';
import InnboksSide from './Innboks';

const KAT_INNTEKT = ['Salg', 'Salg avgiftspliktig 25%', 'Salg avgiftspliktig 15%',
  'Salg fritatt', 'Annen inntekt', 'Renteinntekt'];
const KAT_UTGIFT = ['Husleie', 'Strøm', 'Renhold', 'Programvare', 'Inventar', 'Rekvisita',
  'Regnskap', 'Kontorrekvisita', 'Data/EDB', 'Telefon', 'Internett', 'Reise', 'Reklame',
  'Forsikring', 'Bankgebyr', 'Renter', 'Annet'];

const tomtBilag = () => ({
  dato: idag(), type: 'utgift', kategori: '', beskrivelse: '',
  belop_eks_mva: '', mva_sats: 25, bilagsnummer: '', prosjekt_id: '', hovedkonto: '',
});

const Bilag = () => {
  const aar = new Date().getFullYear();
  const [sp, setSp] = useSearchParams();
  const [fra, setFra] = useState(`${aar}-01-01`);
  const [til, setTil] = useState(idag());
  const [type, setType] = useState('alle');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [kunVis, setKunVis] = useState(false);
  const [sok, setSok] = useState('');
  const [skjema, setSkjema] = useState(tomtBilag());
  const [prosjekter, setProsjekter] = useState([]);
  const [kontoer, setKontoer] = useState([]);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrMelding, setOcrMelding] = useState('');
  const [forslag, setForslag] = useState(null);
  const [fane, setFane] = useState('bilag');     // bilag | ubehandlet
  const [innboksAntall, setInnboksAntall] = useState(0);
  const [innboksEpost, setInnboksEpost] = useState('');
  const [epostKopiert, setEpostKopiert] = useState(false);
  const lastOppRef = useRef(null);
  const [lastOppBusy, setLastOppBusy] = useState(false);

  const lastOppTilInnboks = async (filer) => {
    const arr = Array.from(filer || []);
    if (!arr.length) return;
    setLastOppBusy(true);
    let ok = 0, feil = 0;
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append('fil', f);
        await api.post('/innboks', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch { feil++; }
    }
    setLastOppBusy(false);
    if (lastOppRef.current) lastOppRef.current.value = '';
    try {
      const r = await api.get('/innboks', { params: { status: 'ny' } });
      setInnboksAntall(r.data.length || 0);
    } catch {}
    setFane('ubehandlet');
    if (feil) alert(`${ok} lastet opp, ${feil} feilet`);
  };

  useEffect(() => {
    api.get('/innboks', { params: { status: 'ny' } })
       .then((r) => setInnboksAntall(r.data.length || 0))
       .catch(() => {});
  }, [fane]);

  const ocrLastOpp = async (e) => {
    const fil = e.target.files?.[0]; e.target.value = '';
    if (!fil) return;
    setOcrBusy(true); setOcrMelding('');
    try {
      const fd = new FormData(); fd.append('fil', fil);
      const r = await api.post('/ocr/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const f = r.data || {};
      setSkjema((s) => ({
        ...s,
        dato: f.dato || s.dato,
        beskrivelse: f.beskrivelse || f.leverandor || s.beskrivelse,
        kategori: f.kategori || s.kategori,
        belop_eks_mva: (f.sum && f.mva) ? (f.sum - f.mva).toFixed(2) :
                       (f.sum ? (f.sum / 1.25).toFixed(2) : s.belop_eks_mva),
      }));
      setOcrMelding(`Leste fra kvittering (${f.motor || 'ukjent'}). Sjekk feltene.`);
    } catch (err) {
      const d = err.response?.data;
      if (d?.konfig_mangler) {
        setOcrMelding('OCR ikke konfigurert på server. Be administrator sette OPENAI_API_KEY.');
      } else {
        setOcrMelding(d?.error || 'OCR feilet.');
      }
    } finally { setOcrBusy(false); }
  };

  // Hent konto-/kategoriforslag når kategori/type/beskrivelse endres
  useEffect(() => {
    if (!open) { setForslag(null); return; }
    const t = setTimeout(() => {
      api.get('/forslag/konto', { params: {
        kategori: skjema.kategori,
        type: skjema.type,
        beskrivelse: skjema.beskrivelse,
      } })
        .then((r) => setForslag(r.data)).catch(() => setForslag(null));
    }, 250);
    return () => clearTimeout(t);
  }, [open, skjema.kategori, skjema.type, skjema.beskrivelse]);

  const last = useCallback(() => {
    api.get('/bilag', { params: { fra, til, type } }).then((r) => setData(r.data));
  }, [fra, til, type]);
  useEffect(() => { last(); }, [last]);
  useEffect(() => { api.get('/prosjekt').then((r) => setProsjekter(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/kontoer', { params: { aktive: 1 } }).then((r) => setKontoer(r.data || [])).catch(() => {}); }, []);

  // Auto-åpne bilag for retting via ?rediger=<id> (brukes fra Innboks)
  useEffect(() => {
    const rid = sp.get('rediger');
    if (!rid || !data) return;
    const b = data.bilag?.find((x) => x.id === parseInt(rid, 10));
    if (b) {
      aapneRediger(b, false);
      sp.delete('rediger');
      setSp(sp, { replace: true });
    }
  }, [data]);

  useEffect(() => {
    api.get('/profil/innboks-adresse')
      .then((r) => setInnboksEpost(r.data?.adresse || ''))
      .catch(() => {});
  }, []);

  const kopierEpost = () => {
    if (!innboksEpost) return;
    navigator.clipboard?.writeText(innboksEpost).then(() => {
      setEpostKopiert(true);
      setTimeout(() => setEpostKopiert(false), 1500);
    });
  };

  const aapneNy = () => { setRedigerer(null); setKunVis(false); setSkjema(tomtBilag()); setOpen(true); };
  const aapneRediger = (b, lesbar = false) => {
    setRedigerer(b);
    setKunVis(lesbar);
    setSkjema({
      dato: b.dato, type: b.type, kategori: b.kategori,
      beskrivelse: b.beskrivelse, belop_eks_mva: b.belop_eks_mva,
      mva_sats: b.mva_sats, bilagsnummer: b.bilagsnummer || '',
      prosjekt_id: b.prosjekt_id || '', hovedkonto: b.hovedkonto || '',
    });
    setOpen(true);
  };

  const lagre = async () => {
    const payload = { ...skjema, belop_eks_mva: skjema.belop_eks_mva, mva_sats: skjema.mva_sats };
    if (redigerer) await api.put(`/bilag/${redigerer.id}`, payload);
    else await api.post('/bilag', payload);
    setOpen(false); last();
  };

  const slett = async (b) => {
    if (!window.confirm('Slett dette bilaget?')) return;
    try { await api.delete(`/bilag/${b.id}`); last(); }
    catch (e) {
      const data = e.response?.data;
      if (data?.code === 'MAA_STORNERES') {
        if (window.confirm(data.error + '\n\nLag reversering (stornering) nå?')) storno(b);
      } else { alert(data?.error || 'Feil.'); }
    }
  };

  const storno = async (b) => {
    if (!window.confirm(`Reversere bilag ${b.bilagsnummer || b.id}?\n\nDet opprettes et motbilag (stornering) i dagens dato. Originalbilaget bevares uendret for sporbarhet.`)) return;
    try { await api.post(`/bilag/${b.id}/storno`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feil.'); }
  };

  const filtrert = (data?.bilag || []).filter((b) => {
    if (!sok.trim()) return true;
    const q = sok.trim().toLowerCase();
    const qNum = parseFloat(q.replace(',', '.'));
    if (!isNaN(qNum)) {
      const eks = Number(b.belop_eks_mva || 0);
      const ink = Number(b.belop_ink_mva || 0);
      if ([eks, ink].some((v) =>
        Math.abs(v - qNum) < 0.01 ||
        v.toFixed(2).includes(q) ||
        Math.round(v).toString().includes(q)
      )) return true;
    }
    return [b.beskrivelse, b.kategori, b.bilagsnummer, b.prosjekt_navn]
      .some((v) => (v || '').toString().toLowerCase().includes(q));
  });

  const sortAcc = {
    dato: (b) => b.dato,
    opprettet: (b) => b.opprettet || '',
    type: (b) => b.type,
    kategori: (b) => b.kategori || '',
    beskrivelse: (b) => b.beskrivelse || '',
    prosjekt: (b) => b.prosjekt_navn || '',
    opprettet_av: (b) => b.opprettet_av_navn || b.opprettet_av_epost || '',
    eks: (b) => Number(b.belop_eks_mva || 0),
    mva: (b) => Number(b.mva_belop || 0),
    ink: (b) => Number(b.belop_ink_mva || 0),
  };
  const { sorted: bilagSortert, Th } = useSort(filtrert, { defaultKey: 'dato', defaultDir: 'desc', accessors: sortAcc });

  if (!data) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <PageHeader
        title="Bilag"
        actions={
          <div className="flex items-center" style={{ gap: 8 }}>
            <input ref={lastOppRef} type="file" accept="image/*,.pdf" multiple style={{ display: 'none' }} onChange={(e) => lastOppTilInnboks(e.target.files)} />
            <button className="btn-secondary" onClick={() => lastOppRef.current?.click()} disabled={lastOppBusy}>
              <UploadCloud size={14} /> {lastOppBusy ? 'Laster opp…' : 'Last opp'}
            </button>
            <button className="btn-primary" onClick={aapneNy}><Plus size={14} /> Nytt bilag</button>
          </div>
        }
      />

      <Tabs
        value={fane}
        onChange={setFane}
        items={[
          { key: 'bilag',      label: 'Bilag',      icon: Wallet },
          { key: 'ubehandlet', label: 'Ubehandlet', icon: Inbox, badge: innboksAntall },
        ]}
      />

      {fane === 'ubehandlet' ? <InnboksSide embed /> : (<>
      {innboksEpost && (
        <Card style={{ marginBottom: 12, padding: 12 }}>
          <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'var(--theme-accent-soft, rgba(30,64,175,.1))', color: 'var(--theme-accent)' }}>
              <Mail size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Send bilag på e-post</div>
              <div className="fm-text-muted" style={{ fontSize: 11 }}>
                Videresend kvitteringer og fakturaer hit — de havner under <b>Ubehandlet</b> klare til kontroll.
              </div>
            </div>
            <code style={{ background: 'var(--theme-bg-input)', padding: '6px 10px', borderRadius: 8,
                           fontSize: 13, fontWeight: 600, userSelect: 'all' }}>{innboksEpost}</code>
            <button className="btn-secondary" onClick={kopierEpost}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Copy size={14} /> {epostKopiert ? 'Kopiert!' : 'Kopier'}
            </button>
          </div>
        </Card>
      )}
      <Card style={{ marginBottom: 12 }}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <div><label>Fra</label><input type="date" value={fra} onChange={(e) => setFra(e.target.value)} /></div>
          <div><label>Til</label><input type="date" value={til} onChange={(e) => setTil(e.target.value)} /></div>
          <div>
            <label>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="alle">Alle</option>
              <option value="inntekt">Inntekt</option>
              <option value="utgift">Utgift</option>
            </select>
          </div>
          <div>
            <label>Søk</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%',
                                         transform: 'translateY(-50%)', opacity: 0.55 }} />
              <input value={sok} onChange={(e) => setSok(e.target.value)}
                     placeholder="Beskrivelse, kategori, bilagsnr, beløp…"
                     style={{ paddingLeft: 32 }} />
            </div>
          </div>
        </div>
      </Card>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <StatCard label="Inntekter" value={kr(data.inntekter)} color="var(--theme-success)" icon={TrendingUp} />
        <StatCard label="Utgifter"  value={kr(data.utgifter)}  color="var(--theme-danger)"  icon={TrendingDown} />
        <StatCard label="Resultat"  value={kr(data.resultat)}  color="var(--theme-accent)"  icon={Wallet} />
      </div>

      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              {Th('dato', 'Dato')}
              {Th('opprettet', 'Registrert')}
              {Th('type', 'Type')}
              {Th('kategori', 'Kategori')}
              {Th('beskrivelse', 'Beskrivelse')}
              {Th('prosjekt', 'Prosjekt')}
              {Th('opprettet_av', 'Lagt inn av')}
              {Th('eks', 'Eks. MVA', { align: 'right' })}
              {Th('mva', 'MVA', { align: 'right' })}
              {Th('ink', 'Ink. MVA', { align: 'right' })}
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {bilagSortert.length === 0 && (
              <tr><td colSpan={11} className="fm-text-muted" style={{ textAlign: 'center', padding: 20 }}>Ingen bilag i perioden.</td></tr>
            )}
            {bilagSortert.map((b) => (
              <tr key={b.id}>
                <td>{dato(b.dato)} {b.laast && <Lock size={10} title="Låst periode" />}</td>
                <td className="fm-text-muted" style={{ fontSize: 12 }}
                    title={b.opprettet || ''}>
                  {b.opprettet ? dato(b.opprettet.slice(0, 10)) : '—'}
                </td>
                <td><span className={`badge ${b.type === 'inntekt' ? 'badge-success' : 'badge-danger'}`}>{b.type}</span></td>
                <td>{b.kategori}</td>
                <td>
                  {b.beskrivelse}
                  {b.vedlegg?.length > 0 && (
                    <span className="fm-text-muted" style={{ marginLeft: 6, fontSize: 10 }}>
                      <Paperclip size={10} /> {b.vedlegg.length}
                    </span>
                  )}
                  {b.trenger_kontroll && (
                    <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 10 }}
                          title="OCR-utkast – åpne for å kontrollere før det låses">
                      Kontroller
                    </span>
                  )}
                </td>
                <td>
                  {b.prosjekt_navn ? (
                    <span className="badge badge-neutral" style={{ fontSize: 10 }}>{b.prosjekt_navn}</span>
                  ) : <span className="fm-text-muted">—</span>}
                </td>
                <td>
                  {b.opprettet_av_navn || b.opprettet_av_epost ? (
                    <span title={b.opprettet_av_epost} style={{ fontSize: 12 }}>
                      {b.opprettet_av_navn || b.opprettet_av_epost}
                    </span>
                  ) : <span className="fm-text-muted">—</span>}
                </td>
                <td style={{ textAlign: 'right' }}>{kr(b.belop_eks_mva)}</td>
                <td style={{ textAlign: 'right' }}>{kr(b.mva_belop)}</td>
                <td style={{ textAlign: 'right' }}>{kr(b.belop_ink_mva)}</td>
                <td>
                  <div className="flex" style={{ gap: 4 }}>
                    <button className="btn-ghost" onClick={() => aapneRediger(b, true)} title="Vis bilag"><Eye size={12} /></button>
                    <button className="btn-ghost" onClick={() => aapneRediger(b)}
                            title={b.trenger_kontroll
                                   ? 'Kontroller OCR-utkast – åpne, rett og lagre'
                                   : (b.bokfort && !b.er_siste
                                      ? 'Bokført bilag kan ikke redigeres – bruk reversering'
                                      : (b.bokfort ? 'Rediger siste bilag (logges)' : 'Rediger'))}
                            disabled={b.laast || b.stornert || (b.bokfort && !b.er_siste && !b.trenger_kontroll)}><Pencil size={12} /></button>
                    <button className="btn-ghost" onClick={() => storno(b)} title="Reverser – lag motpostering (stornering)" disabled={b.laast || b.stornert} style={{ color: 'var(--theme-warning)' }}><RotateCcw size={12} /></button>
                    <button className="btn-ghost" onClick={() => slett(b)} title={b.trenger_kontroll ? 'Slett OCR-utkast' : 'Slett'} disabled={b.laast || (b.bokfort && !b.trenger_kontroll)} style={{ color: 'var(--theme-danger)' }}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      </>)}

      <Modal
        open={open} onClose={() => setOpen(false)}
        title={kunVis ? `Vis bilag${redigerer?.bilagsnummer ? ' #' + redigerer.bilagsnummer : ''}` :
               redigerer ? 'Rediger bilag' : 'Nytt bilag'}
        footer={<>
          <button className="btn-ghost" onClick={() => setOpen(false)}>{kunVis ? 'Lukk' : 'Avbryt'}</button>
          {!kunVis && <button className="btn-primary" onClick={lagre}>Lagre</button>}
        </>}
      >
        <fieldset disabled={kunVis} style={{ border: 'none', padding: 0, margin: 0,
                  opacity: kunVis ? 0.95 : 1 }}>
          {kunVis && redigerer?.bokfort && (
            <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6,
                          background: 'rgba(245, 158, 11, 0.12)',
                          border: '1px solid rgba(245, 158, 11, 0.35)',
                          fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lock size={14} />
              <span>
                <strong>Bokført bilag – låst.</strong> I henhold til bokføringsloven §10 kan
                ikke posterte bilag endres. Bruk «Reverser» (stornering) for å lage motbilag og opprett et nytt
                korrigerende bilag.
              </span>
            </div>
          )}
          {!kunVis && redigerer?.bokfort && redigerer?.er_siste && (
            <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6,
                          background: 'rgba(59, 130, 246, 0.12)',
                          border: '1px solid rgba(59, 130, 246, 0.35)',
                          fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Pencil size={14} />
              <span>
                <strong>Retting av siste bilag.</strong> Du kan rette dette bilaget fordi det
                er det siste i bilagsserien. Endringen blir loggført med før/etter-verdier
                for full sporbarhet (bokføringsloven §13b).
              </span>
            </div>
          )}
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <Sparkles size={14} /> {ocrBusy ? 'Leser…' : 'Last opp kvittering (OCR)'}
              <input type="file" accept="image/*,.pdf" onChange={ocrLastOpp} disabled={ocrBusy} style={{ display: 'none' }} />
            </label>
            {ocrMelding && <span className="fm-text-muted" style={{ fontSize: 11 }}>{ocrMelding}</span>}
          </div>
          {forslag?.kategori?.length > 0 && !skjema.kategori && (
            <div style={{ gridColumn: '1 / -1', fontSize: 11 }} className="fm-text-muted">
              <Sparkles size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {forslag.kilde?.some((k) => k.type === 'nokkelord' || k.type === 'historikk')
                ? 'Forslag basert på beskrivelsen:'
                : 'Foreslåtte kategorier:'}{' '}
              {forslag.kategori.slice(0, 5).map((k) => (
                <button key={k.kategori} type="button"
                        className={`badge ${k.historikk ? 'badge-info' : 'badge-neutral'}`}
                        style={{ marginRight: 4, cursor: 'pointer', border: 'none' }}
                        title={k.historikk ? 'Tidligere brukt for liknende beskrivelse' : ''}
                        onClick={() => setSkjema({...skjema, kategori: k.kategori})}>
                  {k.kategori}{k.antall ? ` (${k.antall})` : ''}
                </button>
              ))}
            </div>
          )}
          {forslag?.konto?.length > 0 && skjema.kategori && (
            <div style={{ gridColumn: '1 / -1', fontSize: 11 }} className="fm-text-muted">
              <Sparkles size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Vanlig konto for «{skjema.kategori}»:{' '}
              {forslag.konto.map((k, i) => (
                <span key={k.konto} className="badge badge-neutral" style={{ marginRight: 4 }}>
                  {k.konto}{k.antall ? ` · ${k.antall}×` : k.standard ? ' · standard' : ''}
                </span>
              ))}
            </div>
          )}
          <div><label>Dato</label><input type="date" value={skjema.dato} onChange={(e) => setSkjema({...skjema, dato: e.target.value})} /></div>
          <div>
            <label>Type</label>
            <select value={skjema.type} onChange={(e) => setSkjema({...skjema, type: e.target.value})}>
              <option value="utgift">Utgift</option>
              <option value="inntekt">Inntekt</option>
            </select>
          </div>
          <div>
            <label>Kategori</label>
            <select value={skjema.kategori} onChange={(e) => setSkjema({...skjema, kategori: e.target.value})}>
              <option value="">— velg —</option>
              {(skjema.type === 'inntekt' ? KAT_INNTEKT : KAT_UTGIFT).map((k) =>
                <option key={k} value={k}>{k}</option>
              )}
            </select>
          </div>
          <div>
            <label>Bilagsnummer</label>
            <input
              value={skjema.bilagsnummer || 'Genereres automatisk'}
              readOnly
              disabled
              style={{ background: 'var(--theme-bg-input-disabled, rgba(128,128,128,0.15))', cursor: 'not-allowed', fontStyle: skjema.bilagsnummer ? 'normal' : 'italic', opacity: 0.75 }}
              title="Bilagsnummer settes automatisk for å unngå hull i nummerserien"
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Konto (overstyrer kategori) — la stå tom for automatisk</label>
            <input
              list="bilag-kontoer-list"
              value={skjema.hovedkonto}
              placeholder="f.eks. 7320 Reklame"
              onChange={(e) => {
                const v = e.target.value || '';
                const nr = (v.match(/^\s*(\d{3,5})/) || [])[1] || v.trim();
                setSkjema({ ...skjema, hovedkonto: nr });
              }}
            />
            <datalist id="bilag-kontoer-list">
              {kontoer.map((k) => (
                <option key={k.nummer} value={`${k.nummer} ${k.navn}`} />
              ))}
            </datalist>
          </div>
          <div style={{ gridColumn: '1 / -1' }}><label>Beskrivelse</label><input value={skjema.beskrivelse} onChange={(e) => setSkjema({...skjema, beskrivelse: e.target.value})} /></div>
          <div><label>Beløp (eks. MVA)</label><input value={skjema.belop_eks_mva} onChange={(e) => setSkjema({...skjema, belop_eks_mva: e.target.value})} /></div>
          <div>
            <label>MVA-sats (%)</label>
            <select value={skjema.mva_sats} onChange={(e) => setSkjema({...skjema, mva_sats: parseFloat(e.target.value)})}>
              <option value={25}>25</option><option value={15}>15</option>
              <option value={12}>12</option><option value={0}>0</option>
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Prosjekt</label>
            <select value={skjema.prosjekt_id || ''}
                    onChange={(e) => setSkjema({...skjema, prosjekt_id: e.target.value})}>
              <option value="">— ingen —</option>
              {prosjekter.map((p) => (
                <option key={p.id} value={p.id}>{p.kode ? `${p.kode} · ` : ''}{p.navn}</option>
              ))}
            </select>
          </div>
        </div>
        {redigerer && (
          <Vedleggsliste bilag={redigerer} onChange={last} />
        )}
        {redigerer && (
          <div style={{ marginTop: 14 }}>
            <Kommentarer entitet="bilag" entitetId={redigerer.id} />
          </div>
        )}
        </fieldset>
      </Modal>
    </>
  );
};

const Vedleggsliste = ({ bilag, onChange }) => {
  const [filer, setFiler] = useState(bilag.vedlegg || []);
  const [busy, setBusy] = useState(false);
  const [forhandsvis, setForhandsvis] = useState(null); // {url, mime, filnavn}

  const apneVedlegg = async (v) => {
    try {
      const tok = localStorage.getItem('token');
      const resp = await fetch(`${api.defaults.baseURL}/vedlegg/${v.id}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!resp.ok) throw new Error('Kunne ikke laste vedlegg.');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const mime = v.mime || blob.type || '';
      if (mime.startsWith('image/') || mime === 'application/pdf') {
        setForhandsvis({ url, mime, filnavn: v.filnavn });
      } else {
        window.open(url, '_blank');
      }
    } catch (e) {
      alert(e.message || 'Feil ved åpning av vedlegg.');
    }
  };

  const lukkForhandsvis = () => {
    if (forhandsvis?.url) URL.revokeObjectURL(forhandsvis.url);
    setForhandsvis(null);
  };

  const lastOpp = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('fil', f);
      const r = await api.post(`/bilag/${bilag.id}/vedlegg`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFiler([...filer, r.data]);
      onChange?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Opplasting feilet.');
    }
    setBusy(false);
    e.target.value = '';
  };

  const slett = async (v) => {
    if (!window.confirm(`Slett ${v.filnavn}?`)) return;
    await api.delete(`/vedlegg/${v.id}`);
    setFiler(filer.filter((x) => x.id !== v.id));
    onChange?.();
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--theme-border)' }}>
      <h4 style={{ fontSize: 12, fontWeight: 600, margin: '0 0 8px' }}>
        <Paperclip size={12} /> Vedlegg ({filer.length})
      </h4>
      {filer.map((v) => {
        const mime = v.mime || '';
        const erBilde = mime.startsWith('image/');
        return (
          <div key={v.id}
               style={{ padding: 8, marginBottom: 6, background: 'var(--theme-bg-input)',
                        borderRadius: 4, fontSize: 12 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: erBilde ? 6 : 0 }}>
              <button type="button" onClick={() => apneVedlegg(v)}
                      className="btn-ghost fm-text-primary"
                      style={{ padding: 0, background: 'none', border: 'none',
                               cursor: 'pointer', textAlign: 'left', textDecoration: 'underline' }}>
                {v.filnavn} <span className="fm-text-muted">({Math.round(v.storrelse / 1024)} kB)</span>
              </button>
              <button className="btn-ghost" onClick={() => slett(v)} style={{ color: 'var(--theme-danger)' }}>
                <Trash2 size={12} />
              </button>
            </div>
            {erBilde && (
              <VedleggMiniatyr vedleggId={v.id} onClick={() => apneVedlegg(v)} />
            )}
          </div>
        );
      })}
      <label className="btn-ghost" style={{ cursor: 'pointer', display: 'inline-flex', marginTop: 6 }}>
        <Paperclip size={12} /> {busy ? 'Laster opp…' : 'Last opp fil'}
        <input type="file" hidden onChange={lastOpp} disabled={busy} />
      </label>

      {forhandsvis && (
        <div onClick={lukkForhandsvis}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
                      zIndex: 9999, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', cursor: 'zoom-out', padding: 20 }}>
          <button onClick={(e) => { e.stopPropagation(); lukkForhandsvis(); }}
                  style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)',
                           border: 'none', color: '#fff', borderRadius: 6, padding: '8px 12px',
                           cursor: 'pointer', fontSize: 14 }}>
            <X size={16} style={{ verticalAlign: 'middle' }} /> Lukk
          </button>
          {forhandsvis.mime.startsWith('image/') ? (
            <img src={forhandsvis.url} alt={forhandsvis.filnavn}
                 onClick={(e) => e.stopPropagation()}
                 style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain',
                          cursor: 'default', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }} />
          ) : (
            <iframe src={forhandsvis.url} title={forhandsvis.filnavn}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '95vw', height: '95vh', border: 'none',
                             background: '#fff', borderRadius: 6 }} />
          )}
        </div>
      )}
    </div>
  );
};

const VedleggMiniatyr = ({ vedleggId, onClick }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let aktiv = true;
    let blobUrl = '';
    const tok = localStorage.getItem('token');
    fetch(`${api.defaults.baseURL}/vedlegg/${vedleggId}`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then((r) => r.ok ? r.blob() : null)
      .then((b) => {
        if (!b || !aktiv) return;
        blobUrl = URL.createObjectURL(b);
        setUrl(blobUrl);
      })
      .catch(() => {});
    return () => { aktiv = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [vedleggId]);
  if (!url) return <div className="fm-text-muted" style={{ fontSize: 11 }}>Laster bilde…</div>;
  return (
    <img src={url} alt="vedlegg" onClick={onClick} title="Klikk for å forstørre"
         style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain',
                  background: '#000', borderRadius: 4, cursor: 'zoom-in' }} />
  );
};

export default Bilag;
